import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell, Tooltip, ResponsiveContainer, XAxis, YAxis,
} from 'recharts';
import { RefreshCw, AlertCircle, TrendingUp, FileText } from 'lucide-react';

const COLORS = ['#4CAF50', '#F44336'];
const AUTO_REFRESH_INTERVAL = 60000;

const safeText = (val) => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    if (val.message) return String(val.message);
    if (val.assessment_name) return String(val.assessment_name);
    if (val.name && val.deadline) return `${val.name} — Due: ${new Date(val.deadline).toLocaleDateString()}`;
    if (val.name) return String(val.name);
    try { return JSON.stringify(val); } catch (e) { return 'Object'; }
  }
  return String(val);
};

const formatDeadlineItem = (item) => {
  if (!item) return '—';
  if (typeof item === 'string') return item;
  if (typeof item === 'object') {
    if (item.deadline && item.name) {
      const d = item.deadline ? new Date(item.deadline).toLocaleDateString() : 'N/A';
      return `${item.name} — Due: ${d}`;
    }
    if (item.assessment_name && item.phase) {
      const d = item.deadline ? new Date(item.deadline).toLocaleDateString() : 'N/A';
      return `${item.assessment_name} — Phase: ${item.phase} — Due: ${d}`;
    }
    if (item.message) return item.message;
    return safeText(item);
  }
  return String(item);
};

const COBITAssessmentDashboardPage = () => {
  const navigate = useNavigate();
  const [domainScores, setDomainScores] = useState([]);
  const [answerStats, setAnswerStats] = useState({ yes: 0, no: 0 });
  const [overallScore, setOverallScore] = useState(null);
  const [overview, setOverview] = useState({ domains: 0, processes: 0, questions: 0 });
  const [recentActivities, setRecentActivities] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [userProfile, setUserProfile] = useState({ name: '', department: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      setError(null);
      const { data } = await api.get('maturity/analytics/cobit-dashboard/');

      setDomainScores(Array.isArray(data.domain_scores) ? data.domain_scores : []);
      setAnswerStats({ yes: data.total_yes || 0, no: data.total_no || 0 });

      const capabilityLevel = data.maturity_level ?? null;
      setOverallScore(capabilityLevel);

      setOverview(data.overview || { domains: 0, processes: 0, questions: 0 });
      setRecentActivities(Array.isArray(data.recent_activities) ? data.recent_activities : []);
      setDeadlines(Array.isArray(data.deadlines) ? data.deadlines : []);
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      setUserProfile(data.user_profile || { name: '', department: '', email: '' });

      const assessmentsList = Array.isArray(data.your_assessments) ? data.your_assessments : [];
      const normalizedAssessments = assessmentsList.map((a) => ({
        ...a,
        is_overdue: a.status_label?.includes('Overdue') || false,
        deadline: a.deadline ? new Date(a.deadline).toISOString().split('T')[0] : null,
        status_label: a.status_label || 'On Time ⏰'
      }));
      setAssessments(normalizedAssessments);

      setLastRefreshed(new Date());
      setLoading(false);
    } catch (err) {
      console.error('COBIT dashboard load error:', err?.response || err);
      setError('Failed to load dashboard. Auto-retry in progress...');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();

    const intervalId = setInterval(() => {
      if (isAutoRefreshEnabled) {
        fetchDashboard();
      }
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAutoRefreshEnabled, fetchDashboard]);

  const handleManualRefresh = () => {
    setLoading(true);
    fetchDashboard();
  };

  const handleToggleAutoRefresh = () => {
    setIsAutoRefreshEnabled(!isAutoRefreshEnabled);
  };

  const assessmentCTA = useMemo(() => {
    const items = assessments || [];

    if (!items.length) {
      return {
        label: 'Start Assessment',
        onClick: () => navigate('/cobit/assessment'),
        icon: <TrendingUp size={18} />,
      };
    }

    const hasActive = items.some((a) => !a.status_label?.toLowerCase().includes('submitted'));
    const hasSubmitted = items.some((a) => a.status_label?.toLowerCase().includes('submitted'));

    if (hasActive) {
      return {
        label: 'Continue Assessment',
        onClick: () => navigate('/cobit/assessment'),
        icon: <TrendingUp size={18} />,
      };
    }

    if (hasSubmitted) {
      return {
        label: 'View Report',
        onClick: () => navigate('/cobit/report'),
        icon: <FileText size={18} />,
      };
    }

    return {
      label: 'Start Assessment',
      onClick: () => navigate('/cobit/assessment'),
      icon: <TrendingUp size={18} />,
    };
  }, [assessments, navigate]);

  if (loading && !userProfile?.name && !userProfile?.email) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-orange-700 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const totalResponses = answerStats.yes + answerStats.no;
  const chartData = [...domainScores]
  .map((d) => ({
    code: d.domain_code,
    title: d.domain_name,
    score: Number(d.score) || 0,
  }))
  .sort((a, b) => a.score - b.score);

  return (
    <div className="bg-gradient-to-br from-gray-50 to-orange-50 min-h-screen px-4 sm:px-6 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 bg-white p-6 rounded-xl shadow-lg border-l-4 border-orange-600">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-gray-600 text-sm font-semibold uppercase mb-1">User Name</p>
              <p className="text-2xl font-bold text-orange-700">{userProfile.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm font-semibold uppercase mb-1">Department</p>
              <p className="text-2xl font-bold text-orange-700">{userProfile.department || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm font-semibold uppercase mb-1">Email</p>
              <p className="text-lg font-semibold text-gray-700">{userProfile.email || 'N/A'}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <p>Last updated: <span className="font-semibold">{lastRefreshed.toLocaleTimeString()}</span></p>
              <p className="text-xs text-gray-500 mt-1">Auto-refresh: {isAutoRefreshEnabled ? '🟢 On (60s)' : '🔴 Off'}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleManualRefresh}
                disabled={loading}
                className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition shadow-md"
                title="Manually refresh dashboard"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={handleToggleAutoRefresh}
                className={`px-4 py-2 rounded-lg font-medium transition shadow-md ${
                  isAutoRefreshEnabled
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-400 hover:bg-gray-500 text-white'
                }`}
                title={isAutoRefreshEnabled ? 'Disable auto-refresh' : 'Enable auto-refresh'}
              >
                {isAutoRefreshEnabled ? 'Auto On' : 'Auto Off'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
              <p className="text-red-700 text-sm mt-1">Next attempt in {AUTO_REFRESH_INTERVAL / 1000} seconds</p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-orange-700 mb-2">Welcome, {userProfile.name || 'User'}!</h1>
          <p className="text-gray-600 text-lg">COBIT Capability Assessment Dashboard</p>
        </div>

        <div className="flex flex-wrap gap-4 mb-10">
          <button
            onClick={assessmentCTA.onClick}
            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition transform hover:scale-105 flex items-center gap-2"
          >
            {assessmentCTA.icon}
            {assessmentCTA.label}
          </button>

          <button
            onClick={() => navigate('/cobit/instructions')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition transform hover:scale-105"
          >
            📖 Instructions
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-white border-l-4 border-orange-600 p-6 rounded-lg shadow-lg hover:shadow-xl transition">
            <p className="text-gray-600 text-sm font-semibold uppercase">Processes</p>
            <p className="text-5xl font-bold text-orange-600 mt-2">{overview.domains}</p>
          </div>
          <div className="bg-white border-l-4 border-orange-600 p-6 rounded-lg shadow-lg hover:shadow-xl transition">
            <p className="text-gray-600 text-sm font-semibold uppercase">Governance & Management Practices</p>
            <p className="text-5xl font-bold text-orange-600 mt-2">{overview.processes}</p>
          </div>
          <div className="bg-white border-l-4 border-orange-600 p-6 rounded-lg shadow-lg hover:shadow-xl transition">
            <p className="text-gray-600 text-sm font-semibold uppercase">Questions</p>
            <p className="text-5xl font-bold text-orange-600 mt-2">{overview.questions}</p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-10 rounded-xl shadow-2xl mb-10">
          <p className="text-xl font-semibold opacity-90">Overall Capability Level</p>
          <div className="flex items-end gap-4 mt-4">
            <p className="text-6xl font-bold">{overallScore !== null ? overallScore : 'N/A'}</p>
            {overallScore !== null && (
              <div className="pb-2">
                <p className="text-orange-100 text-sm">on a 0–3 scale</p>
                <div className="w-48 h-3 bg-orange-300 rounded-full mt-2 overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${(overallScore / 3) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          {overallScore !== null && (
            <div className="mt-6 p-4 bg-orange-700 bg-opacity-50 rounded-lg">
              <p className="text-sm">
                {overallScore >= 2.5 ? '✅ Strong governance and management capability' :
                 overallScore >= 2 ? '⚠️ Good capability foundation, with room for improvement' :
                 '🔧 Significant capability improvement opportunities exist'}
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-orange-700 mb-2">
                Process Capability Levels
            </h2>

            <p className="text-sm text-gray-500 mb-6">
                Processes ranked by capability score.
            </p>

            {domainScores.length > 0 ? (
              <ResponsiveContainer width="100%" height={750}>
                  <BarChart
                      layout="vertical"
                      data={chartData}
                      margin={{
                          top: 10,
                          right: 40,
                          left: 30,
                          bottom: 10,
                      }}
                  >
                      <XAxis
                          type="number"
                          domain={[0, 3]}
                          tick={{ fontSize: 12 }}
                      />

                      <YAxis
                          type="category"
                          dataKey="code"
                          width={70}
                          tick={{
                              fontSize: 12,
                              fontWeight: 600,
                          }}
                      />

                      <Tooltip
                          formatter={(value) => [
                              `${(value * 100).toFixed(0)}%`,
                              "Achievement"
                          ]}
                          labelFormatter={(label, payload) => {
                              if (!payload?.length) return label;

                              return `${payload[0].payload.code} - ${payload[0].payload.title}`;
                          }}
                      />

                      <Bar
                          dataKey="score"
                          fill="#FF4D00"
                          radius={[0, 5, 5, 0]}
                      />
                  </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-center py-12">No process capability data available yet.</p>
            )}
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg flex flex-col">
            <h2 className="text-2xl font-bold text-orange-700 mb-6">Response Distribution</h2>

            {(answerStats.yes > 0 || answerStats.no > 0) ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Yes', value: answerStats.yes },
                        { name: 'No', value: answerStats.no },
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      dataKey="value"
                    >
                      {COLORS.map((color, i) => <Cell key={i} fill={color} />)}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} responses`} />
                  </PieChart>
                </ResponsiveContainer>

                <div className="grid grid-cols-2 gap-4 mt-8">
                  <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
                    <p className="text-green-800 font-semibold text-sm">Achieved</p>
                    <p className="text-3xl font-bold text-green-600">{answerStats.yes}</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border-2 border-red-300">
                    <p className="text-red-800 font-semibold text-sm">Not Achieved</p>
                    <p className="text-3xl font-bold text-red-600">{answerStats.no}</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-orange-50 rounded-lg border-2 border-orange-300">
                  <p className="text-gray-700 font-semibold text-sm mb-2">Achievement Rate</p>
                  <div className="w-full bg-gray-300 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                      style={{ width: `${(answerStats.yes / totalResponses) * 100 || 0}%` }}
                    ></div>
                  </div>
                  <p className="text-center font-bold text-orange-700 mt-2">
                    {((answerStats.yes / totalResponses) * 100 || 0).toFixed(1)}%
                  </p>
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-center py-12">No responses recorded yet.</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-orange-700 mb-6">📝 Recent Activity</h2>
            {recentActivities.length > 0 ? (
              <ul className="space-y-3">
                {recentActivities.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                    <span className="text-orange-600 font-bold mt-0.5">•</span>
                    <span className="text-gray-700 text-sm">{safeText(item)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">No recent activity</p>
            )}
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-orange-700 mb-6">⏰ Upcoming Deadlines</h2>
            {deadlines.length > 0 ? (
              <ul className="space-y-3">
                {deadlines.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                    <span className="text-yellow-600 font-bold mt-0.5">!</span>
                    <span className="text-gray-700 text-sm">{formatDeadlineItem(item)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">No upcoming deadlines</p>
            )}
          </div>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-lg mb-12">
          <h2 className="text-2xl font-bold text-orange-700 mb-6">📋 Your Assessments</h2>
          {assessments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-orange-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold text-orange-900">Assessment</th>
                    <th className="px-4 py-3 text-center font-bold text-orange-900">Due Date</th>
                    <th className="px-4 py-3 text-center font-bold text-orange-900">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map((a, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-3 font-semibold text-gray-900">{a.name}</td>
                      <td className="px-4 py-3 text-center text-gray-700">{a.deadline || 'N/A'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                          a.status_label?.includes('Submitted') ? 'bg-green-600' :
                          a.status_label?.includes('Overdue') ? 'bg-red-600' :
                          'bg-orange-600'
                        }`}>
                          {a.status_label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 italic">No assessments available</p>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="bg-white p-8 rounded-xl shadow-lg mb-12">
            <h2 className="text-2xl font-bold text-orange-700 mb-6">🔔 Notifications</h2>
            <ul className="space-y-3">
              {notifications.map((n, i) => (
                <li key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <span className="text-blue-600 font-bold mt-0.5">✓</span>
                  <span className="text-gray-700 text-sm">{safeText(n)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-center py-8 border-t border-gray-300">
          <p className="text-gray-600 mb-2">Questions or need assistance?</p>
          <button
            onClick={() => navigate('/cobit/instructions')}
            className="text-orange-600 font-semibold hover:text-orange-700 underline"
          >
            📖 View Instructions
          </button>
        </div>
      </div>
    </div>
  );
};

export default COBITAssessmentDashboardPage;